import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';

interface Doc {
  id: number;
  title: string;
  description: string | null;
  body_markdown: string | null;
  has_markdown: boolean;
  has_pdf: boolean;
  uploaded_at: string;
  uploaded_by_name: string | null;
}

const ADMIN_ROLES = ['Owner', 'Admin'];

const TrainingReader: React.FC = () => {
  const { user, sessionToken } = useAuth();
  const { id } = useParams<{ id: string }>();
  const isAdmin = ADMIN_ROLES.includes(user?.role || '');
  const [doc, setDoc] = useState<Doc | null>(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [preview, setPreview] = useState(false);
  const [sectionQuery, setSectionQuery] = useState('');
  const bookmarkKey = `cafeops-reading:${user?.id}:${id}`;
  const [bookmark, setBookmark] = useState(() => { try { return sessionStorage.getItem(bookmarkKey) || ''; } catch { return ''; } });
  const headingId = (text: string) => `section-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const remember = (anchor: string) => { setBookmark(anchor); try { sessionStorage.setItem(bookmarkKey, anchor); } catch {} };
  useEditProtection(editing && draft !== (doc?.body_markdown || ''));

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/training-documents/${id}`, {
          cache: 'no-store',
          headers: { 'X-Session-Token': sessionToken || '' },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setDoc(data.document);
        setDraft(data.document?.body_markdown || '');
      } catch (e: any) {
        setErr(e.message);
      }
    })();
  }, [id, sessionToken]);

  const save = async () => {
    if (!doc) return;
    try {
      setSaving(true);
      setSaveMsg('');
      const res = await fetch(`/api/training-documents/${doc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
        body: JSON.stringify({ body_markdown: draft }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSaveMsg('Saved.');
      setDoc({ ...doc, body_markdown: draft, has_markdown: !!draft });
      setEditing(false);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (err && !doc) return <div className="error-banner">{err}</div>;
  if (!doc) return <p>Loading…</p>;

  const pdfUrl = doc.has_pdf
    ? `/api/training-documents/${doc.id}/file?token=${encodeURIComponent(sessionToken || '')}`
    : null;

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/training">← All training</Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <div>
          <h1 style={{ margin: '0 0 0.25rem' }}>{doc.title}</h1>
          {doc.description && <p style={{ color: '#666', margin: 0 }}>{doc.description}</p>}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="salary-btn secondary">
              View original PDF
            </a>
          )}
          {isAdmin && !editing && (
            <button className="salary-btn secondary" onClick={() => setEditing(true)}>Edit content</button>
          )}
        </div>
      </div>

      {err && <div className="error-banner" role="alert">{err}</div>}
      {saveMsg && (
        <div style={{ background: '#e3f9e5', borderLeft: '4px solid #4caf50', padding: '0.5rem 1rem', marginBottom: '1rem' }}>
          {saveMsg}
        </div>
      )}

      {!editing && doc.body_markdown && <details><summary>Contents &amp; resume</summary>
        <input type="search" aria-label="Search document sections" placeholder="Find a section" value={sectionQuery} onChange={e => setSectionQuery(e.target.value)} />
        {bookmark && <p><a href={`#${bookmark}`}>Resume saved section</a></p>}
        <ul>{doc.body_markdown.split('\n').filter(line => /^#{1,3} /.test(line)).map((line, index) => { const text = line.replace(/^#+ /, ''); const anchor = headingId(text); return text.toLowerCase().includes(sectionQuery.toLowerCase()) ? <li key={index}><a href={`#${anchor}`} onClick={() => remember(anchor)}>{text}</a></li> : null; })}</ul>
      </details>}
      {editing ? (
        <div className="salary-card">
          <p style={{ color: '#666', marginTop: 0 }}>
            Markdown is supported (headings <code>#</code>, bullets <code>-</code>, bold <code>**text**</code>, tables, etc.).
          </p>
<button onClick={() => setPreview(!preview)}>{preview ? 'Edit Markdown' : 'Preview'}</button>
          {preview ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft}</ReactMarkdown> : <textarea
            aria-label="Training content" disabled={saving}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={28}
            style={{ width: '100%', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize: '0.9rem' }}
          />}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="salary-btn" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="salary-btn secondary" onClick={() => { if (draft !== (doc.body_markdown || '') && !window.confirm('Discard unsaved content?')) return; setEditing(false); setDraft(doc.body_markdown || ''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="markdown-content" style={{ background: '#fff', padding: '1rem 1.25rem', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}>
          {doc.body_markdown ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              h1: ({ children }) => <h1 id={headingId(String(children))}>{children}</h1>,
              h2: ({ children }) => <h2 id={headingId(String(children))}>{children}</h2>,
              h3: ({ children }) => <h3 id={headingId(String(children))}>{children}</h3>,
            }}>{doc.body_markdown}</ReactMarkdown>
          ) : (
            <p style={{ color: '#666' }}>
              No written content yet. {pdfUrl && (<>Use <a href={pdfUrl} target="_blank" rel="noreferrer">View original PDF</a> to read the source document.</>)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TrainingReader;
